/**
 * IDTPRO - Storefront helpers v7
 *
 * Tre funzioni distinte in un solo file (caricato sempre, gestisce
 * tutto lato client):
 *
 * 1) IVA scorporata B2B + suffix "+ IVA"
 *    Trigger: body.is-b2b-customer
 *    Sorgenti prezzi: [samitaws-product-price] (Sami Wholesale) e
 *    fallback su .price-item--regular/--last/--sale per pagine
 *    senza Sami. Divide TUTTI i text node numerici, anche i barrati
 *    (compare_at) cosi' il "prima/dopo" e' coerente.
 *    Idempotenza via WeakMap sui text node (no piu' loop).
 *    Anti-flicker: body.idt-iva-ready aggiunto dopo 1.2s o subito
 *    se non-B2B.
 *
 * 2) Card "Aggiungi al carrello" via fetch /cart/add.js
 *    Trigger: submit di .ronix-card-cta-form (sui card delle PLP).
 *    Niente piu' redirect alla PDP: POST AJAX, feedback inline sul
 *    bottone ("AGGIUNTO ✓"), refresh del contatore in header.
 *
 * 3) Selettore quantita' delle card (- 1 +)
 *    Trigger: click su .ronix-card-qty-decr / .ronix-card-qty-incr.
 *    Modifica il value dell'input number sibling.
 *
 * Debug: window.__idtIva.debug()
 */
(function () {
  'use strict';

  const VAT_RATE = 1.22;
  const SUFFIX_CLASS = 'idt-iva-suffix';
  const SAMI_SELECTOR = '[samitaws-product-price]';
  const FALLBACK_SELECTOR =
    '.price-item--regular, .price-item--last, .price-item--sale';

  // WeakMap text-node -> ultimo netto calcolato (float).
  const processedNodes = new WeakMap();

  function isB2B() {
    return document.body.classList.contains('is-b2b-customer');
  }

  function parseEuro(str) {
    if (!str) return null;
    const m = str.match(/([\d]+[\d.,]*)/);
    if (!m) return null;
    let s = m[1];
    if (s.indexOf(',') > -1) {
      s = s.replace(/\./g, '').replace(',', '.');
    }
    const v = parseFloat(s);
    return isNaN(v) || v <= 0 ? null : v;
  }

  function formatEuro(n) {
    return n
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      .replace(/\.(\d{2})$/, ',$1');
  }

  function processTextNode(node) {
    const currentText = node.nodeValue.trim();
    if (!currentText) return false;
    const value = parseEuro(currentText);
    if (value === null) return false;

    const lastNetto = processedNodes.get(node);
    if (lastNetto !== undefined && Math.abs(value - lastNetto) < 0.05) {
      return false;
    }

    const netto = value / VAT_RATE;
    const formatted = '€' + formatEuro(netto);

    processedNodes.set(node, netto);
    node.nodeValue = formatted;
    return true;
  }

  function collectPriceTextNodes(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) {
      const txt = node.nodeValue.trim();
      if (!txt) continue;
      if (parseEuro(txt) === null) continue;
      nodes.push(node);
    }
    return nodes;
  }

  function ensureSuffixOnPriceContainer(el) {
    // Trova il piu' vicino .price ancestor (o l'el stesso se e' .price)
    let priceRoot = el.closest('.price');
    if (!priceRoot) priceRoot = el;
    // Niente suffix su .price--sold-out o .price con .price-item--unavailable
    if (priceRoot.querySelector('.price-item--unavailable')) return;
    if (priceRoot.querySelector('.' + SUFFIX_CLASS)) return;
    const suffix = document.createElement('span');
    suffix.className = SUFFIX_CLASS;
    suffix.dataset.idtIvaSuffix = '';
    suffix.textContent = '+ IVA';
    priceRoot.appendChild(suffix);
  }

  function processElement(el) {
    if (!el || !isB2B()) return 0;
    const nodes = collectPriceTextNodes(el);
    if (nodes.length === 0) return 0;
    let processed = 0;
    nodes.forEach(function (n) {
      if (processTextNode(n)) processed++;
    });
    el.setAttribute('data-idt-iva-done', '1');
    // Sblocco per-card: marca .card-wrapper ancestor come "ready"
    const cardWrap = el.closest('.card-wrapper, .product-card-wrapper');
    if (cardWrap) {
      cardWrap.setAttribute('data-idt-iva-card-ready', '1');
    }
    ensureSuffixOnPriceContainer(el);
    return processed;
  }

  function processSamiElements() {
    if (!isB2B()) return 0;
    let count = 0;
    document.querySelectorAll(SAMI_SELECTOR).forEach(function (el) {
      count += processElement(el);
    });
    return count;
  }

  function processFallbackPriceItems() {
    // Solo per pagine SENZA Sami (es. prodotti senza pricing rule).
    if (!isB2B()) return 0;
    if (document.querySelector(SAMI_SELECTOR)) return 0;
    let count = 0;
    document.querySelectorAll(FALLBACK_SELECTOR).forEach(function (el) {
      // Niente price-item--unavailable ("Prezzo su richiesta")
      if (el.classList.contains('price-item--unavailable')) return;
      count += processElement(el);
    });
    return count;
  }

  function processAll() {
    const processed = processSamiElements() + processFallbackPriceItems();
    tryMarkReady();
    return processed;
  }

  function markReady() {
    document.body.classList.add('idt-iva-ready');
  }

  function tryMarkReady() {
    if (document.body.classList.contains('idt-iva-ready')) return;
    const total = document.querySelectorAll(SAMI_SELECTOR).length;
    if (total === 0) {
      // Nessun Sami su questa pagina: se ci sono fallback elementi processati, sblocca
      const fbDone = document.querySelectorAll(FALLBACK_SELECTOR + '[data-idt-iva-done]').length;
      if (fbDone > 0) markReady();
      return;
    }
    const done = document.querySelectorAll(SAMI_SELECTOR + '[data-idt-iva-done]').length;
    if (done >= total) markReady();
  }

  // ----- 2) Cart Ajax intercept -------------------------------------

  function refreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const count = cart.item_count;
        document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(function (el) {
          const span = el.querySelector('span[aria-hidden]') || el;
          if (span) span.textContent = count;
        });
        // Visibility se Dawn nasconde cart count quando == 0
        document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
          if (count > 0) el.removeAttribute('hidden');
        });
      })
      .catch(function () { /* swallow */ });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    const opener = document.querySelector('#cart-icon-bubble, a[href="/cart"]');
    if (opener && drawer) {
      opener.click();
      return true;
    }
    return false;
  }

  function flashButton(btn, text, durationMs) {
    if (!btn) return;
    if (!btn.dataset.originalText) {
      btn.dataset.originalText = btn.textContent.trim();
    }
    btn.disabled = true;
    btn.classList.add('ronix-card-cta--success');
    btn.textContent = text;
    setTimeout(function () {
      btn.disabled = false;
      btn.classList.remove('ronix-card-cta--success');
      btn.textContent = btn.dataset.originalText;
    }, durationMs || 2000);
  }

  function handleCardAddClick(event) {
    const btn = event.target.closest('.ronix-card-cta-add');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();

    if (btn.disabled) return;

    const wrap = btn.closest('.ronix-card-cta-wrap');
    const variantId = btn.dataset.variantId;
    if (!variantId) {
      console.warn('[IDTPRO Cart] missing data-variant-id', btn);
      return;
    }

    let quantity = 1;
    if (wrap) {
      const qtyInput = wrap.querySelector('.ronix-card-qty-input');
      if (qtyInput) {
        const v = parseInt(qtyInput.value, 10);
        if (!isNaN(v) && v > 0) quantity = v;
      }
    }

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/javascript' }
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (result) {
        if (result.ok && result.data && result.data.id) {
          flashButton(btn, 'AGGIUNTO ✓', 2000);
          refreshCartCount();
          openCartDrawer();
        } else {
          flashButton(btn, 'ERRORE — RIPROVA', 2500);
          console.warn('[IDTPRO Cart] add failed:', result.data);
        }
      })
      .catch(function (err) {
        flashButton(btn, 'ERRORE — RIPROVA', 2500);
        console.error('[IDTPRO Cart] network error:', err);
      });
  }

  // ----- 3b) Tier widget real-time update --------------------------

  // Stessa tabella tier definita lato Liquid (main-cart-footer.liquid).
  // Tenuta in sync manualmente: se cambi i tier Sami, aggiorna qui.
  const TIER_TABLE = {
    rivenditore: [
      { threshold: 0,    current: 20,   next: 22.5, nextAt: 1001 },
      { threshold: 1001, current: 22.5, next: 25,   nextAt: 2501 },
      { threshold: 2501, current: 25,   next: 28,   nextAt: 5001 },
      { threshold: 5001, current: 28,   next: null, nextAt: null }
    ],
    professionista: [
      { threshold: 0,    current: 10,   next: 12.5, nextAt: 1001 },
      { threshold: 1001, current: 12.5, next: 15,   nextAt: 2501 },
      { threshold: 2501, current: 15,   next: 18,   nextAt: 5001 },
      { threshold: 5001, current: 18,   next: null, nextAt: null }
    ]
  };

  function tierFor(label, subtotalEur) {
    const key = (label || '').toLowerCase();
    const table = TIER_TABLE[key];
    if (!table) return null;
    let pick = table[0];
    for (let i = 0; i < table.length; i++) {
      if (subtotalEur >= table[i].threshold) pick = table[i];
    }
    return pick;
  }

  function moneyEur(cents) {
    const n = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + n.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function updateTierWidget(cart) {
    const widget = document.querySelector('[data-tier-widget]');
    if (!widget) return;
    const label = widget.dataset.tierLabel;
    if (!label) return;

    const subtotalCents = cart.total_price;
    const subtotalEur = subtotalCents / 100;
    const tier = tierFor(label, subtotalEur);
    if (!tier) return;

    const savedCents = Math.round((subtotalCents * tier.current) / 100);
    const discountedCents = subtotalCents - savedCents;

    const setText = function (sel, txt) {
      const el = widget.querySelector(sel);
      if (el) el.textContent = txt;
    };
    setText('[data-tier-current-percent]', tier.current);
    setText('[data-tier-discounted]', moneyEur(discountedCents));
    setText('[data-tier-saved]', moneyEur(savedCents));

    const deltaLine = widget.querySelector('[data-tier-delta-line]');
    const progressTrack = widget.querySelector('[data-tier-progress-track]');
    const progressFill = widget.querySelector('[data-tier-progress-fill]');

    if (tier.next !== null && tier.nextAt !== null) {
      const nextAtCents = tier.nextAt * 100;
      const deltaCents = Math.max(0, nextAtCents - subtotalCents);
      let progress = (subtotalCents / nextAtCents) * 100;
      if (progress > 100) progress = 100;
      if (deltaLine) {
        deltaLine.innerHTML =
          '<strong>Aggiungi <span data-tier-delta>' + moneyEur(deltaCents) + '</span></strong><br>' +
          '<span>per sbloccare lo sconto −<span data-tier-next-percent>' + tier.next + '</span>%</span>';
      }
      if (progressTrack) progressTrack.style.display = '';
      if (progressFill) {
        progressFill.style.width = progress.toFixed(2) + '%';
        progressFill.dataset.progress = progress.toFixed(2);
      }
    } else {
      if (deltaLine) {
        deltaLine.innerHTML = '<strong>🏆 Hai raggiunto il massimo sconto!</strong>';
      }
      if (progressTrack) progressTrack.style.display = 'none';
    }
  }

  function refreshTierWidget() {
    if (!document.querySelector('[data-tier-widget]')) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(updateTierWidget)
      .catch(function () { /* swallow */ });
  }

  // Refresh del widget tier: fetch dell'intera pagina /cart (l'API
  // section_id Shopify dava 404 in modalita' preview password-protected
  // - vedere log 'GET /cart?section_id=cart-footer 404'). Parsifichiamo
  // l'HTML completo e estraiamo solo #main-cart-footer per il replace.
  function refreshCartFooterSection() {
    if (!document.getElementById('main-cart-footer')) return;
    fetch('/cart', { credentials: 'include', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newFooter = doc.querySelector('#main-cart-footer');
        const oldFooter = document.getElementById('main-cart-footer');
        if (newFooter && oldFooter) {
          oldFooter.replaceWith(newFooter);
          if (typeof window.__idtBindTotalsObserver === 'function') {
            window.__idtBindTotalsObserver();
          }
          // Riapplica i colori inline dopo il replace
          setTimeout(forceCartTheme, 50);
        }
      })
      .catch(function (err) {
        console.warn('[IDTPRO Tier] refresh failed:', err);
      });
  }

  // ----- 3) Card quantity stepper -----------------------------------

  function handleQuantityClick(event) {
    const btn = event.target.closest('.ronix-card-qty-decr, .ronix-card-qty-incr');
    if (!btn) return;
    event.preventDefault();
    const wrap = btn.closest('.ronix-card-quantity');
    if (!wrap) return;
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    let val = parseInt(input.value, 10);
    if (isNaN(val)) val = 1;
    const min = parseInt(input.min || '1', 10);
    const max = parseInt(input.max || '999', 10);
    if (btn.classList.contains('ronix-card-qty-decr')) {
      val = Math.max(min, val - 1);
    } else {
      val = Math.min(max, val + 1);
    }
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ----- Init -------------------------------------------------------

  // ----- Cart force-color JS: l'ultimo CSS Dawn vince per ordine di
  // load anche con !important pareggiato. Forziamo inline style con
  // priority "important" per battere qualsiasi cosa.
  function isCartPage() {
    return window.location.pathname === '/cart'
      || window.location.pathname.indexOf('/cart/') === 0
      || document.body.classList.contains('template-cart')
      || document.querySelector('cart-items') !== null;
  }

  function forceCartTheme() {
    if (!isCartPage()) return;

    const whiteSelector = [
      'cart-items .cart-item__name',
      'cart-items a.cart-item__name',
      'cart-items .cart-item__details',
      'cart-items td.cart-item__details',
      'cart-items thead th',
      'cart-items .cart-items__heading',
      'cart-items .title',
      'cart-items .title--primary',
      'cart-items h1',
      'cart-items h1.title',
      '#main-cart-footer .totals__total',
      '#main-cart-footer h2.totals__total',
      'cart-items .product-option',
      'cart-items .product-option dt',
      'cart-items .product-option dd'
    ].join(', ');
    document.querySelectorAll(whiteSelector).forEach(function (el) {
      el.style.setProperty('color', '#ffffff', 'important');
    });

    const redSelector = [
      'cart-items .price-item--regular',
      'cart-items .price-item',
      'cart-items td.cart-item__totals',
      'cart-items td.cart-item__totals *',
      'cart-items dd.cart-item__final-price',
      'cart-items dd.cart-item__final-price *',
      'cart-items .cart-item__final-price',
      'cart-items .cart-item__price-wrapper .price',
      'cart-items .cart-item__price-wrapper .price *',
      '#main-cart-footer .totals__total-value',
      '#main-cart-footer p.totals__total-value'
    ].join(', ');
    document.querySelectorAll(redSelector).forEach(function (el) {
      el.style.setProperty('color', '#e32219', 'important');
      el.style.setProperty('font-weight', '800', 'important');
    });

    // Continua lo shopping = rosso brand
    document.querySelectorAll('cart-items a.underlined-link').forEach(function (el) {
      el.style.setProperty('color', '#e32219', 'important');
    });

    // Barra tier widget: forza altezza e visibilita' (aumentate per
    // essere ben evidente anche con width <20%).
    const track = document.querySelector('.idt-tier-widget [data-tier-progress-track]');
    if (track) {
      track.style.setProperty('display', 'block', 'important');
      track.style.setProperty('height', '14px', 'important');
      track.style.setProperty('width', '100%', 'important');
      track.style.setProperty('border-radius', '7px', 'important');
      track.style.setProperty('background-color', 'rgba(255,255,255,0.22)', 'important');
      track.style.setProperty('border', '1px solid rgba(255,255,255,0.18)', 'important');
      track.style.setProperty('margin-top', '1.4rem', 'important');
      track.style.setProperty('overflow', 'hidden', 'important');
    }
    const fill = document.querySelector('.idt-tier-widget [data-tier-progress-fill]');
    if (fill) {
      fill.style.setProperty('display', 'block', 'important');
      fill.style.setProperty('height', '100%', 'important');
      fill.style.setProperty('background', 'linear-gradient(90deg, #e32219 0%, #ff4d54 100%)', 'important');
      fill.style.setProperty('box-shadow', '0 0 10px rgba(227,34,25,0.75)', 'important');
      fill.style.setProperty('min-width', '8px', 'important');
      fill.style.setProperty('transition', 'width 600ms cubic-bezier(0.4,0,0.2,1)', 'important');
      fill.style.setProperty('border-radius', '7px', 'important');
    }
  }

  function init() {
    // Cart Ajax + quantity stepper sempre attivi (anche non-B2B).
    document.addEventListener('click', handleCardAddClick, true);
    document.addEventListener('click', handleQuantityClick, true);

    // Cart force theme: chiamato 1 volta all'init + 1 volta dopo che
    // Dawn ha completato il primo render. Niente polling continuo
    // (rischio di loop con altri observer).
    if (isCartPage()) {
      forceCartTheme();
      setTimeout(forceCartTheme, 800);
    }

    // Tier widget: Dawn fa GIA' il replace del widget via cart.js
    // (getSectionsToRender include selector '.idt-tier-widget').
    // Il nuovo HTML Liquid arriva con i valori aggiornati (subtotale
    // scontato, delta, width barra). MA gli inline style che
    // forceCartTheme ha applicato vengono distrutti dal replace.
    // Quindi: dopo cart change, ri-applichiamo forceCartTheme per
    // ripristinare colori + altezza barra.
    if (isCartPage()) {
      // Hook primario: Dawn pubblica 'cart-update' su pubsub.js DOPO
      // aver completato i replace di sezione. Ascoltarlo direttamente
      // e' piu' affidabile di setTimeout cieco.
      if (typeof window.subscribe === 'function') {
        window.subscribe('cart-update', function () {
          setTimeout(forceCartTheme, 50);
        });
      } else if (typeof subscribe === 'function') {
        // fallback: subscribe e' definita globale ma non su window
        try {
          subscribe('cart-update', function () {
            setTimeout(forceCartTheme, 50);
          });
        } catch (e) { /* swallow */ }
      }
      // Fallback su user action (in caso pubsub non funzioni o
      // qualcun altro intercetti il cart change).
      let themeDebounce = null;
      function scheduleForceTheme(delayMs) {
        if (themeDebounce) clearTimeout(themeDebounce);
        themeDebounce = setTimeout(forceCartTheme, delayMs || 700);
      }
      document.addEventListener('change', function (e) {
        if (e.target.closest('cart-items .quantity__input, .cart-item .quantity__input')) {
          scheduleForceTheme(700);
        }
      });
      document.addEventListener('click', function (e) {
        if (e.target.closest('cart-remove-button, .quantity__button')) {
          scheduleForceTheme(900);
        }
      }, true);
    }

    if (!isB2B()) {
      markReady();
      window.__idtIva = { isB2B: false };
      return;
    }

    processAll();

    function onSamiComplete() {
      setTimeout(processAll, 50);
      setTimeout(processAll, 300);
    }
    document.addEventListener('samitaWSRenderWholesaleCompleted', onSamiComplete);
    window.addEventListener('samitaWSRenderWholesaleCompleted', onSamiComplete);

    setTimeout(processAll, 500);
    setTimeout(processAll, 1500);

    // Safety net globale: dopo 2.5s sblocca TUTTI i prezzi via
    // body.idt-iva-ready (la regola CSS forza visibility:visible
    // su tutto). Meglio mostrare prezzo sbagliato che pagina vuota.
    setTimeout(markReady, 2500);

    // Per-card fallback: dopo 1.5s, per le card senza Sami elements,
    // processiamo i .price-item Liquid direttamente e marchiamo ready.
    // Niente safety net globale: meglio una card sola che non si
    // sblocca, che 20 card che cambiano valore davanti agli occhi.
    setTimeout(function () {
      document
        .querySelectorAll(
          '.card-wrapper:not([data-idt-iva-card-ready]), .product-card-wrapper:not([data-idt-iva-card-ready])'
        )
        .forEach(function (cardWrap) {
          if (cardWrap.querySelector(SAMI_SELECTOR)) return; // aspetta Sami
          const items = cardWrap.querySelectorAll(FALLBACK_SELECTOR);
          items.forEach(function (el) {
            if (el.classList.contains('price-item--unavailable')) return;
            processElement(el);
          });
          // Anche senza prezzi numerici (es. "Prezzo su richiesta"), sblocca
          cardWrap.setAttribute('data-idt-iva-card-ready', '1');
        });
      // PDP fallback: se body non e' ancora ready, sblocca comunque
      tryMarkReady();
      if (!document.body.classList.contains('idt-iva-ready')) {
        // Se PDP ha un prezzo non Sami (es. Sami non e' caricato per il PDP),
        // processo manualmente e marko ready
        const pdpPrice = document.querySelector('.product__info-wrapper .price-item--last, .product__info-wrapper .price-item--regular');
        if (pdpPrice && !pdpPrice.classList.contains('price-item--unavailable')) {
          processElement(pdpPrice);
        }
        markReady();
      }
    }, 1500);

    window.__idtIva = {
      isB2B: true,
      version: '7.0',
      reprocess: processAll,
      ready: function () { return document.body.classList.contains('idt-iva-ready'); },
      debug: function () {
        const samiEls = document.querySelectorAll(SAMI_SELECTOR);
        const fbEls = document.querySelectorAll(FALLBACK_SELECTOR);
        console.log('B2B:', isB2B(), '| ready:', this.ready());
        console.log('Sami elements:', samiEls.length, '| fallback elements:', fbEls.length);
        const suffixCount = document.querySelectorAll('.' + SUFFIX_CLASS).length;
        console.log('Suffix "+ IVA" nel DOM:', suffixCount);
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
